import type { ApplicationEnvironment, ApplicationWithStats, User } from '@owl/shared';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../lib/api';
import { type ApiHealth, pingApiHealth } from '../lib/apiHealth';
import { type AppHealth, aggregateAppsHealth } from '../lib/appHealth';
import { hashToRoute, routeToHash } from '../lib/route';
import { applyTheme, readSettingsTheme } from '../lib/theme';
import { AiAppDetail } from '../views/AiAppDetail';
import { AiModels } from '../views/AiModels';
import { AiPrices } from '../views/AiPrices';
import { AiTokens } from '../views/AiTokens';
import { AiUsage } from '../views/AiUsage';
import { Alerts } from '../views/Alerts';
import { AppDashboard } from '../views/AppDashboard';
import { Applications } from '../views/Applications';
import { AuditDetail } from '../views/AuditDetail';
import { Audits } from '../views/Audits';
import { Benchmark } from '../views/Benchmark';
import { BenchmarkDetail } from '../views/BenchmarkDetail';
import { Conversations } from '../views/Conversations';
import { MessageTypes } from '../views/MessageTypes';
import { Notifications } from '../views/Notifications';
import { Overview } from '../views/Overview';
import { RailwayCosts } from '../views/RailwayCosts';
import { Settings } from '../views/Settings';
import { Users } from '../views/Users';
import { Versions } from '../views/Versions';
import { ChangePasswordModal } from './ChangePasswordModal';
import { Icon } from './icons';
import { Brand } from './shared';

const HEALTH_POLL_MS = 15_000;

export type Route =
  | { name: 'overview' }
  | { name: 'applications' }
  | {
      name: 'appdash';
      applicationId: string;
      logId?: string;
      prefill?: { messageTypeIds?: string[] };
    }
  | { name: 'messagetypes' }
  | {
      name: 'alerts';
      prefill?: { applicationId?: string; messageTypeId?: string; pattern?: string };
    }
  | { name: 'aitokens' }
  | { name: 'aiusage' }
  | { name: 'aiappdetail'; applicationId: string }
  | { name: 'aimodels' }
  | { name: 'aiprices' }
  | { name: 'railwaycosts' }
  | { name: 'benchmark' }
  | { name: 'benchmarkdetail'; runId: string }
  | { name: 'conversations' }
  | { name: 'audits' }
  | { name: 'auditdetail'; id: string }
  | { name: 'notifications' }
  | { name: 'settings' }
  | { name: 'users' }
  | { name: 'versions' };

export type Range = '1h' | '6h' | '24h' | '7d' | '30d' | 'mtd';

export type ShellCtx = {
  navigate: (r: Route) => void;
  range: Range;
  setRange: (r: Range) => void;
  refreshTick: number;
  triggerRefresh: () => void;
  autoRefreshSeconds: number | null;
  setAutoRefreshSeconds: (s: number | null) => void;
  environment: ApplicationEnvironment;
  setEnvironment: (e: ApplicationEnvironment) => void;
  user: User;
  setUser: (u: User) => void;
};

const ENV_STORAGE_KEY = 'owl.env';
const AUTO_REFRESH_STORAGE_KEY = 'owl.autoRefresh';
const DEFAULT_AUTO_REFRESH_SECONDS = 5;

function loadInitialEnvironment(): ApplicationEnvironment {
  if (typeof window === 'undefined') return 'production';
  const v = window.localStorage.getItem(ENV_STORAGE_KEY);
  return v === 'development' ? 'development' : 'production';
}

function loadInitialAutoRefresh(): number | null {
  if (typeof window === 'undefined') return DEFAULT_AUTO_REFRESH_SECONDS;
  const v = window.localStorage.getItem(AUTO_REFRESH_STORAGE_KEY);
  if (v === null) return DEFAULT_AUTO_REFRESH_SECONDS;
  if (v === 'off') return null;
  const n = Number.parseInt(v, 10);
  if (!Number.isFinite(n) || n <= 0) return DEFAULT_AUTO_REFRESH_SECONDS;
  return n;
}

const AUTO_REFRESH_OPTIONS: { label: string; value: number | null }[] = [
  { label: 'Off', value: null },
  { label: '5s', value: 5 },
  { label: '10s', value: 10 },
  { label: '30s', value: 30 },
  { label: '60s', value: 60 },
];

export function Shell({
  user,
  onLogout,
  onUserChange,
}: {
  user: User;
  onLogout: () => void;
  onUserChange: (u: User) => void;
}) {
  const [route, setRoute] = useState<Route>(() => hashToRoute(window.location.hash));

  const navigate = useCallback((next: Route) => {
    setRoute(next);
    const hash = routeToHash(next);
    // Assigning the hash pushes a history entry, which is what lets Back return here.
    if (window.location.hash !== hash) window.location.hash = hash;
  }, []);

  useEffect(() => {
    const onHashChange = () => {
      const hash = window.location.hash;
      // A change navigate() made has already set the route, prefill included, and
      // re-parsing it would throw the prefill away. Only Back/Forward or an edited URL
      // arrive here with a hash the current route does not already produce.
      setRoute((current) => (routeToHash(current) === hash ? current : hashToRoute(hash)));
    };
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);
  const [range, setRange] = useState<Range>('24h');
  const [refreshTick, setRefreshTick] = useState(0);
  const [autoRefreshSeconds, setAutoRefreshSecondsState] = useState<number | null>(
    loadInitialAutoRefresh,
  );
  const [environment, setEnvironmentState] =
    useState<ApplicationEnvironment>(loadInitialEnvironment);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [apiHealth, setApiHealth] = useState<ApiHealth>('checking');
  const [apiLatencyMs, setApiLatencyMs] = useState<number | null>(null);
  const [appsHealth, setAppsHealth] = useState<{
    worst: AppHealth;
    downCount: number;
    warnCount: number;
  }>({
    worst: 'ok',
    downCount: 0,
    warnCount: 0,
  });
  const intervalRef = useRef<number | null>(null);
  const healthIntervalRef = useRef<number | null>(null);

  const setEnvironment = useCallback((e: ApplicationEnvironment) => {
    setEnvironmentState(e);
    try {
      window.localStorage.setItem(ENV_STORAGE_KEY, e);
    } catch {
      // ignore
    }
  }, []);

  const setAutoRefreshSeconds = useCallback((s: number | null) => {
    setAutoRefreshSecondsState(s);
    try {
      window.localStorage.setItem(AUTO_REFRESH_STORAGE_KEY, s === null ? 'off' : String(s));
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.getSettings();
        if (cancelled) return;
        applyTheme(readSettingsTheme(r.settings));
      } catch {
        // Fall back to defaults; the CSS vars stay at their boot values.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (intervalRef.current !== null) {
      window.clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (autoRefreshSeconds === null) return;
    intervalRef.current = window.setInterval(() => {
      setRefreshTick((t) => t + 1);
    }, autoRefreshSeconds * 1000);
    return () => {
      if (intervalRef.current !== null) {
        window.clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [autoRefreshSeconds]);

  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      const [ping, apps] = await Promise.all([
        pingApiHealth(),
        api
          .listApplications()
          .then((r) => r.items)
          .catch(() => [] as ApplicationWithStats[]),
      ]);
      if (cancelled) return;
      setApiHealth(ping.state);
      setApiLatencyMs(ping.latencyMs);
      setAppsHealth(aggregateAppsHealth(apps));
    };
    tick();
    healthIntervalRef.current = window.setInterval(tick, HEALTH_POLL_MS);
    return () => {
      cancelled = true;
      if (healthIntervalRef.current !== null) {
        window.clearInterval(healthIntervalRef.current);
        healthIntervalRef.current = null;
      }
    };
  }, []);

  const triggerRefresh = useCallback(() => setRefreshTick((t) => t + 1), []);

  const connState: 'ok' | 'warn' | 'down' | 'checking' =
    apiHealth === 'checking'
      ? 'checking'
      : apiHealth === 'down'
        ? 'down'
        : apiHealth === 'slow'
          ? 'warn'
          : appsHealth.worst === 'down'
            ? 'down'
            : appsHealth.worst === 'warn'
              ? 'warn'
              : 'ok';

  const connTitle =
    apiHealth === 'checking'
      ? 'Comprobando estado…'
      : apiHealth === 'down'
        ? 'API caída'
        : apiHealth === 'slow'
          ? `API lenta · ${apiLatencyMs?.toFixed(0)}ms`
          : appsHealth.downCount > 0
            ? `${appsHealth.downCount} app${appsHealth.downCount === 1 ? '' : 's'} sin logs >1h`
            : appsHealth.warnCount > 0
              ? `${appsHealth.warnCount} app${appsHealth.warnCount === 1 ? '' : 's'} sin logs >10min`
              : `API saludable · ${apiLatencyMs?.toFixed(0) ?? '—'}ms`;

  const ctx = useMemo<ShellCtx>(
    () => ({
      navigate,
      range,
      setRange,
      refreshTick,
      triggerRefresh,
      autoRefreshSeconds,
      setAutoRefreshSeconds,
      environment,
      setEnvironment,
      user,
      setUser: onUserChange,
    }),
    [
      navigate,
      range,
      refreshTick,
      triggerRefresh,
      autoRefreshSeconds,
      setAutoRefreshSeconds,
      environment,
      setEnvironment,
      user,
      onUserChange,
    ],
  );

  const isViewer = user.role === 'viewer';
  const isSuperadmin = user.role === 'superadmin';

  const isActive = (n: Route['name']) =>
    route.name === n || (n === 'applications' && route.name === 'appdash');

  function navItem(
    name: Exclude<Route['name'], 'appdash'>,
    label: string,
    IconC: typeof Icon.Overview,
  ) {
    const badge =
      name === 'applications' && appsHealth.worst !== 'ok' ? (
        <span
          className={`nav-badge ${appsHealth.worst}`}
          title={
            appsHealth.downCount > 0
              ? `${appsHealth.downCount} app${appsHealth.downCount === 1 ? '' : 's'} sin logs >1h`
              : `${appsHealth.warnCount} app${appsHealth.warnCount === 1 ? '' : 's'} sin logs >10min`
          }
        />
      ) : null;
    return (
      <button
        type="button"
        key={name}
        className={`nav-item ${isActive(name) ? 'active' : ''}`}
        onClick={() => {
          navigate({ name } as Route);
          setSidebarOpen(false);
        }}
      >
        <IconC className="nav-icon" />
        {label}
        {badge}
      </button>
    );
  }

  return (
    <div className={`app-shell env-${environment}`}>
      {sidebarOpen && (
        <button
          type="button"
          className="sidebar-overlay"
          aria-label="Close navigation"
          onClick={() => setSidebarOpen(false)}
        />
      )}
      <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-head">
          <Brand />
        </div>
        <nav className="sidebar-nav">
          {navItem('overview', 'Overview', Icon.Overview)}
          {navItem('applications', 'Applications', Icon.Apps)}
          {!isViewer && navItem('versions', 'Control de versiones', Icon.Server)}
          {navItem('messagetypes', 'Message types', Icon.Types)}
          {navItem('alerts', 'Alerts', Icon.Alerts)}
          <div style={{ height: 24 }} />
          {navItem('aiusage', 'AI Dashboard', Icon.Spark)}
          {!isViewer && navItem('aimodels', 'AI models', Icon.Types)}
          {!isViewer && navItem('aiprices', 'AI prices', Icon.Coin)}
          {navItem('aitokens', 'AI tokens', Icon.Coin)}
          <div style={{ height: 24 }} />
          {navItem('railwaycosts', 'Railway costs', Icon.Coin)}
          {navItem('benchmark', 'Benchmarks', Icon.Spark)}
          {navItem('conversations', 'Conversations', Icon.Spark)}
          {navItem('audits', 'Audits', Icon.Spark)}
          <div style={{ height: 24 }} />
          {navItem('notifications', 'Notifications', Icon.Alerts)}
          <div style={{ flex: 1, minHeight: 24 }} />
          {isSuperadmin && navItem('users', 'Users', Icon.Apps)}
          {!isViewer && navItem('settings', 'Settings', Icon.Setup)}
        </nav>
        <div className="sidebar-foot" style={{ position: 'relative' }}>
          <span className={`conn-dot ${connState}`} title={connTitle} />
          <button
            type="button"
            className="conn-info"
            onClick={() => setUserMenuOpen((v) => !v)}
            style={{
              background: 'transparent',
              border: 0,
              padding: 0,
              cursor: 'pointer',
              textAlign: 'left',
              color: 'inherit',
            }}
          >
            <div className="conn-status">{user.email}</div>
            <div className="conn-region">
              {user.role === 'superadmin'
                ? 'Super admin'
                : user.role === 'admin'
                  ? 'Admin'
                  : 'Viewer'}
            </div>
          </button>
          <button type="button" className="icon-btn" title="Sign out" onClick={onLogout}>
            <Icon.LogOut size={14} />
          </button>
          {userMenuOpen && (
            <div
              style={{
                position: 'absolute',
                bottom: 'calc(100% + 4px)',
                left: 8,
                right: 8,
                background: 'var(--bg-2)',
                border: '1px solid var(--border-1)',
                borderRadius: 8,
                padding: 4,
                zIndex: 10,
              }}
              onMouseLeave={() => setUserMenuOpen(false)}
            >
              <button
                type="button"
                className="nav-item"
                style={{ width: '100%' }}
                onClick={() => {
                  setUserMenuOpen(false);
                  setChangingPassword(true);
                }}
              >
                Change password
              </button>
            </div>
          )}
        </div>
        <div className="sidebar-credit">
          Powered by{' '}
          <a href="https://usenorlia.com" target="_blank" rel="noreferrer">
            Norlia.ai
          </a>
          <span className="sidebar-credit-version mono"> · v{__APP_VERSION__}</span>
        </div>
      </aside>

      <main className="main">
        <header className="topbar">
          <button
            type="button"
            className="topbar-hamburger"
            onClick={() => setSidebarOpen((v) => !v)}
            title="Menu"
            aria-label="Toggle navigation"
          >
            <Icon.Menu size={18} />
          </button>
          <div className="crumbs">
            <span>OWL</span>
            <span className="crumb-sep">/</span>
            <strong>{titleFor(route)}</strong>
          </div>
          <div className="range-picker env-picker" style={{ marginLeft: 16 }} title="Environment">
            <button
              type="button"
              className={`range-opt range-opt-env ${environment === 'development' ? 'active' : ''}`}
              onClick={() => setEnvironment('development')}
            >
              Dev
            </button>
            <button
              type="button"
              className={`range-opt range-opt-env ${environment === 'production' ? 'active' : ''}`}
              onClick={() => setEnvironment('production')}
            >
              Prod
            </button>
          </div>
          <div style={{ flex: 1 }} />
          <div className="topbar-right">
            <Clock />
            <button
              type="button"
              className="icon-btn"
              title="Refresh data"
              onClick={triggerRefresh}
            >
              <Icon.Refresh size={14} />
            </button>
            <div className="range-picker" title="Auto-refresh">
              {AUTO_REFRESH_OPTIONS.map((opt) => (
                <button
                  type="button"
                  key={opt.label}
                  className={`range-opt ${autoRefreshSeconds === opt.value ? 'active' : ''}`}
                  onClick={() => setAutoRefreshSeconds(opt.value)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </header>

        {route.name === 'overview' && <Overview ctx={ctx} />}
        {route.name === 'applications' && <Applications ctx={ctx} />}
        {route.name === 'versions' && <Versions ctx={ctx} />}
        {route.name === 'appdash' && (
          <AppDashboard
            ctx={ctx}
            applicationId={route.applicationId}
            logId={route.logId}
            prefill={route.prefill}
          />
        )}
        {route.name === 'messagetypes' && <MessageTypes ctx={ctx} />}
        {route.name === 'alerts' && <Alerts ctx={ctx} prefill={route.prefill} />}
        {route.name === 'aitokens' && <AiTokens ctx={ctx} />}
        {route.name === 'aiusage' && <AiUsage ctx={ctx} />}
        {route.name === 'aiappdetail' && (
          <AiAppDetail ctx={ctx} applicationId={route.applicationId} />
        )}
        {route.name === 'aimodels' && <AiModels ctx={ctx} />}
        {route.name === 'aiprices' && <AiPrices ctx={ctx} />}
        {route.name === 'railwaycosts' && <RailwayCosts ctx={ctx} />}
        {route.name === 'benchmark' && <Benchmark ctx={ctx} />}
        {route.name === 'benchmarkdetail' && <BenchmarkDetail ctx={ctx} runId={route.runId} />}
        {route.name === 'conversations' && <Conversations ctx={ctx} />}
        {route.name === 'audits' && <Audits ctx={ctx} />}
        {route.name === 'auditdetail' && <AuditDetail ctx={ctx} runId={route.id} />}
        {route.name === 'notifications' && <Notifications ctx={ctx} />}
        {route.name === 'settings' && <Settings ctx={ctx} />}
        {route.name === 'users' && <Users ctx={ctx} currentUser={user} />}
      </main>
      {changingPassword && <ChangePasswordModal onClose={() => setChangingPassword(false)} />}
    </div>
  );
}

function Clock() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(id);
  }, []);
  const time = new Intl.DateTimeFormat('es-ES', {
    timeZone: 'Europe/Madrid',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).format(now);
  return (
    <span
      className="mono"
      style={{
        fontSize: 11.5,
        color: 'var(--fg-2)',
        background: 'var(--bg-2)',
        border: '1px solid var(--border-1)',
        borderRadius: 8,
        padding: '4px 10px',
        letterSpacing: '0.04em',
      }}
      title="Europe/Madrid"
    >
      {time}
    </span>
  );
}

function titleFor(route: Route): string {
  switch (route.name) {
    case 'overview':
      return 'Overview';
    case 'applications':
      return 'Applications';
    case 'versions':
      return 'Control de versiones';
    case 'appdash':
      return 'Application';
    case 'messagetypes':
      return 'Message types';
    case 'alerts':
      return 'Alerts';
    case 'aitokens':
      return 'AI tokens';
    case 'aiusage':
      return 'AI Dashboard';
    case 'aiappdetail':
      return 'AI app detail';
    case 'aimodels':
      return 'AI models';
    case 'aiprices':
      return 'AI prices';
    case 'railwaycosts':
      return 'Railway costs';
    case 'benchmark':
      return 'Benchmarks';
    case 'benchmarkdetail':
      return 'Benchmark run';
    case 'conversations':
      return 'Conversations';
    case 'audits':
      return 'Audits';
    case 'auditdetail':
      return 'Audit run';
    case 'notifications':
      return 'Notifications';
    case 'settings':
      return 'Settings';
    case 'users':
      return 'Users';
  }
}
